import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, CreditCard } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '../store/useAppStore'
import { api } from '../lib/api'

interface UserStats {
  recent_transactions: {
    ref_code: string
    amount_baht: number
    status: string
    created_at: string
    completed_at: string | null
  }[]
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  completed: { label: 'สำเร็จ', color: 'var(--color-success)' },
  pending_review: { label: 'รอตรวจ', color: 'var(--color-warning)' },
  awaiting_slip: { label: 'รอสลิป', color: 'var(--color-text-muted)' },
  rejected: { label: 'ไม่ผ่าน', color: 'var(--color-danger)' },
  expired: { label: 'หมดอายุ', color: 'var(--color-text-soft)' },
}

function formatDateTime(s: string): string {
  const d = new Date(s)
  const months = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
  const date = `${d.getDate()} ${months[d.getMonth()]} ${(d.getFullYear() + 543).toString().slice(-2)}`
  const time = `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
  return `${date} • ${time}`
}

export default function TransactionsPage() {
  const navigate = useNavigate()
  const { lineUid } = useAppStore()

  const { data: stats, isLoading } = useQuery({
    queryKey: ['user', 'transactions', lineUid],
    queryFn: () => api.user.stats(lineUid!) as Promise<UserStats>,
    enabled: !!lineUid,
    staleTime: 1000 * 60,
  })

  return (
    <div className="flex flex-col min-h-dvh pb-4"
         style={{ background: 'var(--color-bg)' }}>

      <div className="flex items-center gap-3 px-4 pt-6 pb-4">
        <button onClick={() => navigate('/profile')}
                style={{ color: 'var(--color-text-muted)' }}>
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>
          ประวัติการชำระเงิน
        </h1>
      </div>

      <div className="mx-4 card-amulet p-3 flex flex-col gap-2">
        {isLoading && (
          <div className="text-center py-8 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            กำลังโหลด...
          </div>
        )}

        {!isLoading && (!stats?.recent_transactions || stats.recent_transactions.length === 0) && (
          <div className="text-center py-8 text-sm" style={{ color: 'var(--color-text-soft)' }}>
            ยังไม่มีประวัติการชำระเงิน
          </div>
        )}

        {stats?.recent_transactions?.map(tx => {
          const status = STATUS_LABELS[tx.status] ?? { label: tx.status, color: 'var(--color-text-muted)' }
          return (
            <div key={tx.ref_code} className="flex items-center gap-3 p-3 rounded-xl"
                 style={{ background: 'var(--color-surface-2)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                   style={{ background: 'var(--color-surface)' }}>
                <CreditCard size={18} style={{ color: 'var(--color-gold-dark)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold font-mono"
                     style={{ color: 'var(--color-text)' }}>
                  {tx.ref_code}
                </div>
                <div className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
                  {formatDateTime(tx.created_at)}
                </div>
              </div>
              <div className="flex flex-col items-end">
                <div className="text-base font-bold" style={{ color: 'var(--color-text)' }}>
                  {tx.amount_baht}฿
                </div>
                <div className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                     style={{ background: `${status.color}22`, color: status.color }}>
                  {status.label}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <p className="text-center text-[10px] mt-4 px-4" style={{ color: 'var(--color-text-soft)' }}>
        แสดงเฉพาะ 5 รายการล่าสุด
      </p>
    </div>
  )
}
